import React, { useEffect, useState } from 'react';
import { Box, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Key, Plugs, UsersThree } from 'phosphor-react';
import { toast } from 'sonner';

import { IMcpChave, IMcpChaveCriada, IMcpSessaoItem, useMcp } from '../../../hooks/useMcp';
import { api } from '../../../services/api';
import { detalheDoErro, formatarDataHora, urlDoMcp } from '../mcpUtils';
import {
  Aviso,
  BlocoMono,
  Bolinha,
  BotaoPerigo,
  BotaoPrimario,
  BotaoSecundario,
  Cartao,
  Etiqueta,
  Eyebrow,
  IconeRedondo,
  Interruptor,
  Linha,
  MONO,
  Recolhivel,
  TEXTO,
  TEXTO_SUAVE,
} from '../ui';

const POLLING_MS = 15000;

// A chave só aparece uma vez (criação ou rotação): daqui ela some.
function ChaveRevelada({ chave, onFechar }: { chave: IMcpChaveCriada; onFechar: () => void }) {
  return (
    <Flex
      direction="column"
      gap="10px"
      p="14px 16px"
      borderRadius="16px"
      bg="rgba(220,252,231,.7)"
      boxShadow="inset 0 0 0 1px #A7F3D0"
    >
      <Text fontSize="13px" fontWeight={700} color="#166534">
        Chave &quot;{chave.nome}&quot; pronta. Copie agora: ela não será mostrada de novo.
      </Text>
      <BlocoMono copiar>{chave.chave}</BlocoMono>
      {chave.sessoes_revogadas > 0 && (
        <Text fontSize="12.5px" color="#166534">
          {chave.sessoes_revogadas} sessão(ões) da chave antiga foram derrubadas.
        </Text>
      )}
      <Flex>
        <BotaoSecundario onClick={onFechar}>Já copiei</BotaoSecundario>
      </Flex>
    </Flex>
  );
}

function LinhaChave({
  chave,
  onGirada,
}: {
  chave: IMcpChave;
  onGirada: (nova: IMcpChaveCriada) => void;
}) {
  const { girarChave, ativarChave } = useMcp();
  const cliente = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);

  // a confirmação de "girar" some sozinha: um clique perdido não derruba as sessões
  useEffect(() => {
    if (!confirmando) return undefined;
    const t = setTimeout(() => setConfirmando(false), 6000);
    return () => clearTimeout(t);
  }, [confirmando]);

  const atualizar = () => {
    cliente.invalidateQueries({ queryKey: ['mcp', 'chaves'] });
    cliente.invalidateQueries({ queryKey: ['mcp', 'sessoes'] });
  };

  const girar = useMutation({
    mutationFn: () => girarChave(chave.id),
    onSuccess: resposta => {
      setConfirmando(false);
      onGirada(resposta.data);
      atualizar();
    },
    onError: erro => toast.error(detalheDoErro(erro) ?? 'Não foi possível girar a chave.'),
  });
  const alternar = useMutation({
    mutationFn: () => ativarChave(chave.id, !chave.ativo),
    onSuccess: () => {
      toast.success(chave.ativo ? 'Chave desativada e sessões derrubadas.' : 'Chave ativada.');
      atualizar();
    },
    onError: erro => toast.error(detalheDoErro(erro) ?? 'Não foi possível alterar a chave.'),
  });

  return (
    <Linha opacity={chave.ativo ? 1 : 0.65}>
      <Box flex="1 1 220px" minW={0}>
        <Text fontSize="13.5px" fontWeight={700} color={TEXTO}>
          {chave.nome}
        </Text>
        <Text fontFamily={MONO} fontSize="12px" color={TEXTO_SUAVE}>
          {chave.prefixo}…
        </Text>
      </Box>
      <Box flex="1 1 160px" minW={0}>
        <Text fontSize="12.5px" color="#334155">
          {chave.sessoes_ativas} sessão(ões) ativa(s)
        </Text>
        <Text fontSize="12px" color={TEXTO_SUAVE}>
          último uso {formatarDataHora(chave.ultimo_uso)}
        </Text>
      </Box>
      <Etiqueta tom={chave.ativo ? 'sucesso' : 'neutro'}>
        {chave.ativo ? 'ativa' : 'desativada'}
      </Etiqueta>
      {confirmando ? (
        <BotaoPerigo isLoading={girar.isPending} onClick={() => girar.mutate()}>
          Confirmar: trocar a chave e derrubar {chave.sessoes_ativas} sessão(ões)
        </BotaoPerigo>
      ) : (
        <BotaoSecundario onClick={() => setConfirmando(true)}>Girar chave</BotaoSecundario>
      )}
      <Interruptor
        ligado={chave.ativo}
        desabilitado={alternar.isPending}
        onChange={() => alternar.mutate()}
        rotulo={`Chave ${chave.nome} ativa`}
      />
    </Linha>
  );
}

function SecaoChaves() {
  const { getChaves, criarChave } = useMcp();
  const cliente = useQueryClient();
  const [nome, setNome] = useState('');
  const [revelada, setRevelada] = useState<IMcpChaveCriada | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['mcp', 'chaves'],
    queryFn: getChaves,
    refetchInterval: POLLING_MS,
  });
  const chaves = data?.data ?? [];

  const criar = useMutation({
    mutationFn: () => criarChave(nome.trim()),
    onSuccess: resposta => {
      setRevelada(resposta.data);
      setNome('');
      cliente.invalidateQueries({ queryKey: ['mcp', 'chaves'] });
    },
    onError: erro => toast.error(detalheDoErro(erro) ?? 'Não foi possível criar a chave.'),
  });

  return (
    <Cartao p={0} overflow="hidden">
      <Flex direction="column" gap="12px" p="18px 20px 14px">
        <Flex align="center" gap="12px">
          <IconeRedondo icone={<Key size={18} weight="bold" />} tom="info" />
          <Box>
            <Eyebrow>LOGIN 1 · ENTRADA DA REDE</Eyebrow>
            <Text fontSize="16px" fontWeight={700} color={TEXTO}>
              Chaves da rede
            </Text>
          </Box>
        </Flex>
        {revelada && <ChaveRevelada chave={revelada} onFechar={() => setRevelada(null)} />}
        <Flex gap="8px" wrap="wrap">
          <Input
            maxW="320px"
            h="40px"
            borderRadius="10px"
            bg="white"
            borderColor="#CBD5E1"
            fontSize="14px"
            placeholder="Nome da chave (ex.: Assistente da rede)"
            value={nome}
            maxLength={80}
            onChange={e => setNome(e.target.value)}
          />
          <BotaoPrimario
            h="40px"
            isDisabled={nome.trim().length === 0}
            isLoading={criar.isPending}
            onClick={() => criar.mutate()}
          >
            Nova chave
          </BotaoPrimario>
        </Flex>
      </Flex>

      {isLoading && (
        <Flex justify="center" py={6}>
          <Spinner />
        </Flex>
      )}
      {isError && (
        <Box p="0 20px 16px">
          <Aviso tom="perigo">Não foi possível carregar as chaves.</Aviso>
        </Box>
      )}
      {!isLoading && !isError && chaves.length === 0 && (
        <Linha>
          <Text fontSize="13px" color={TEXTO_SUAVE}>
            Nenhuma chave criada. Sem chave, ninguém consegue entrar no MCP.
          </Text>
        </Linha>
      )}
      {chaves.map(c => (
        <LinhaChave key={c.id} chave={c} onGirada={setRevelada} />
      ))}
    </Cartao>
  );
}

function LinhaSessao({ s }: { s: IMcpSessaoItem }) {
  const { revogarSessao } = useMcp();
  const cliente = useQueryClient();
  const revogar = useMutation({
    mutationFn: () => revogarSessao(s.id),
    onSuccess: () => {
      toast.success('Sessão revogada.');
      cliente.invalidateQueries({ queryKey: ['mcp', 'sessoes'] });
      cliente.invalidateQueries({ queryKey: ['mcp', 'chaves'] });
    },
    onError: erro => toast.error(detalheDoErro(erro) ?? 'Não foi possível revogar.'),
  });

  let estado = 'expirada';
  let tom: 'sucesso' | 'neutro' | 'perigo' = 'neutro';
  if (s.revogada) {
    estado = 'revogada';
    tom = 'perigo';
  } else if (s.ativa) {
    estado = 'ativa';
    tom = 'sucesso';
  }

  return (
    <Linha opacity={s.ativa ? 1 : 0.6}>
      <Bolinha texto={s.usuario_nome ?? 'MCP'} tom={s.usuario_nome ? 'info' : 'neutro'} />
      <Box flex="1 1 220px" minW={0}>
        <Text fontSize="13px" fontWeight={600} color={TEXTO}>
          {s.usuario_nome ?? 'Só a chave'}
          {s.usuario_nome && (
            <Text as="span" fontWeight={400} color={TEXTO_SUAVE}>
              {' '}
              (até {formatarDataHora(s.dt_expira_usuario)})
            </Text>
          )}
        </Text>
        <Text fontSize="12px" color={TEXTO_SUAVE}>
          sessão #{s.id} · chave {s.chave_nome ?? '-'}
        </Text>
      </Box>
      <Text fontFamily={MONO} fontSize="11.5px" color={TEXTO_SUAVE}>
        {s.ip_login}
      </Text>
      <Box flex="1 1 170px" minW={0}>
        <Text fontSize="12px" color="#475569">
          aberta {formatarDataHora(s.dt_criacao)}
        </Text>
        <Text fontSize="12px" color={TEXTO_SUAVE}>
          expira {formatarDataHora(s.dt_expira)}
        </Text>
      </Box>
      <Etiqueta tom={tom}>{estado}</Etiqueta>
      {s.ativa && (
        <BotaoPerigo isLoading={revogar.isPending} onClick={() => revogar.mutate()}>
          Revogar
        </BotaoPerigo>
      )}
    </Linha>
  );
}

function SecaoSessoes() {
  const { getSessoes } = useMcp();
  const { data, isLoading } = useQuery({
    queryKey: ['mcp', 'sessoes'],
    queryFn: getSessoes,
    refetchInterval: POLLING_MS,
  });
  const sessoes = data?.data ?? [];

  return (
    <Cartao p={0} overflow="hidden">
      <Flex align="center" gap="12px" p="18px 20px 12px">
        <IconeRedondo icone={<UsersThree size={18} weight="bold" />} tom="info" />
        <Box>
          <Eyebrow>QUEM ESTÁ CONECTADO</Eyebrow>
          <Text fontSize="16px" fontWeight={700} color={TEXTO}>
            Sessões abertas
          </Text>
        </Box>
      </Flex>
      {isLoading && (
        <Flex justify="center" py={6}>
          <Spinner />
        </Flex>
      )}
      {!isLoading && sessoes.length === 0 && (
        <Linha>
          <Text fontSize="13px" color={TEXTO_SUAVE}>
            Nenhuma sessão ainda.
          </Text>
        </Linha>
      )}
      {sessoes.map(s => (
        <LinhaSessao key={s.id} s={s} />
      ))}
    </Cartao>
  );
}

function ComoConectar() {
  const base = urlDoMcp(api.defaults.baseURL, '/mcp/');
  const login = `${base.replace(/\/+$/, '')}/login/`;
  return (
    <Recolhivel
      icone={<Plugs size={16} />}
      titulo="Como conectar a IA"
      resumo="2 passos: chave da rede, depois a senha do usuário"
    >
      <Flex gap="12px" align="flex-start">
        <Bolinha texto="1" />
        <Box flex={1} minW={0}>
          <Text fontSize="13.5px" fontWeight={700} color={TEXTO} mb="6px">
            Chave da rede, token de 1 dia (só ferramentas gerais)
          </Text>
          <BlocoMono copiar>{`POST ${login}\n{"chave": "mcpk_..."}`}</BlocoMono>
        </Box>
      </Flex>
      <Flex gap="12px" align="flex-start">
        <Bolinha texto="2" />
        <Box flex={1} minW={0}>
          <Text fontSize="13.5px" fontWeight={700} color={TEXTO} mb="6px">
            Senha do MCP do usuário (libera o que o usuário já pode no sistema)
          </Text>
          <BlocoMono copiar>tools/call → mcp_autenticar_usuario</BlocoMono>
        </Box>
      </Flex>
    </Recolhivel>
  );
}

export function ChavesSessoesTab() {
  return (
    <Flex direction="column" gap="16px">
      <ComoConectar />
      <SecaoChaves />
      <SecaoSessoes />
    </Flex>
  );
}
